import React, { useState, useContext, useEffect } from "react";
import { getApiBaseURL } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { AuthContext } from "../../contexts/AuthContext";

const EditRoleForm = ({ role, onCancel, onUpdated }) => {
  const [formData, setFormData] = useState({
    role: role.role || "",
    role_description: role.role_description || "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const apiBase = getApiBaseURL();
  const { user } = useContext(AuthContext);
  const isSystemRole = ["admin", "super_admin"].includes(
    role.role.toLowerCase()
  );

  useEffect(() => {
    // Track if form has changes
    const changed =
      formData.role !== role.role ||
      formData.role_description !== (role.role_description || "");
    setHasChanges(changed);
  }, [formData, role]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.role.trim()) {
      setError("Role name is required");
      return;
    }

    if (!hasChanges) {
      setError("No changes to save");
      return;
    }

    // Prevent changing system role names
    if (
      isSystemRole &&
      formData.role.toLowerCase() !== role.role.toLowerCase()
    ) {
      setError("Cannot change system role names");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // HIPAA Compliant: Using secureAxios with httpOnly cookies
      await secureAxios.put(`${apiBase}/api/roles/${role.role_id}`, {
        role: formData.role.trim(),
        role_description: formData.role_description.trim(),
      });

      alert("✅ Role updated successfully");
      if (onUpdated) onUpdated();
    } catch (err) {
      console.error("❌ Error updating role:", err);
      setError(err.response?.data?.error || "Failed to update role");
    } finally {
      setLoading(false);
    }
  };

  // Check authorization
  if (user?.role !== "admin") {
    return (
      <div className="alert alert-info" role="alert">
        Only administrators can edit roles.
      </div>
    );
  }

  return (
    <div>
      <h4>Edit Role</h4>

      {error && (
        <div className="alert alert-danger alert-dismissible" role="alert">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
            aria-label="Close"
          ></button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="row g-3 mt-3">
        <div className="col-md-6">
          <label htmlFor="edit-role-name" className="form-label">
            Role Name <span className="text-danger">*</span>
            {isSystemRole && (
              <span className="badge bg-warning text-dark ms-2">
                System Role
              </span>
            )}
          </label>
          <input
            id="edit-role-name"
            name="role"
            className="form-control"
            value={formData.role}
            onChange={handleChange}
            required
            maxLength={50}
            disabled={loading || isSystemRole}
          />
          {isSystemRole && (
            <div className="form-text text-warning">
              System role names cannot be changed
            </div>
          )}
        </div>

        <div className="col-12">
          <label htmlFor="edit-role-description" className="form-label">
            Description
          </label>
          <textarea
            id="edit-role-description"
            name="role_description"
            className="form-control"
            value={formData.role_description}
            onChange={handleChange}
            rows="3"
            maxLength={500}
            placeholder="Describe the permissions and responsibilities of this role"
            disabled={loading}
          />
          <div className="form-text">
            {formData.role_description.length}/500 characters
          </div>
        </div>

        {/* Audit Information */}
        {(role.created_at || role.updated_at) && (
          <div className="col-12">
            <div className="card bg-light">
              <div className="card-body py-2">
                <small className="text-muted">
                  <strong>Audit Trail:</strong>
                  <br />
                  {role.created_at && (
                    <>Created: {new Date(role.created_at).toLocaleString()}</>
                  )}
                  {role.updated_at && role.updated_at !== role.created_at && (
                    <>
                      <br />
                      Last updated: {new Date(role.updated_at).toLocaleString()}
                    </>
                  )}
                </small>
              </div>
            </div>
          </div>
        )}

        <div className="col-12 d-flex justify-content-between">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <div>
            <button
              type="button"
              className="btn btn-outline-secondary me-2"
              onClick={() =>
                setFormData({
                  role: role.role || "",
                  role_description: role.role_description || "",
                })
              }
              disabled={loading || !hasChanges}
            >
              Reset
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !formData.role.trim() || !hasChanges}
            >
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </div>
      </form>

      {/* Security Notice */}
      <div className="alert alert-warning mt-3" role="alert">
        <i className="bi bi-exclamation-triangle"></i>{" "}
        <strong>Security Notice:</strong>
        Modifying roles affects system access control. All changes are logged
        for security audit.
      </div>
    </div>
  );
};

export default EditRoleForm;
